import React, { Component } from 'react';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { createResourceLocatorString } from '../../util/routes';
import { isMainSearchTypeKeywords } from '../../util/search';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import chart1 from '../../assets/chart-1.jpg';
import chart2 from '../../assets/chart-2.jpg';

import css from './NilfluenceScorePage.module.css';

export class NilfluenceScorePageComponent extends Component {
  constructor(props) {
    super(props);
    // The StaticRouter component used in server side rendering
    // provides the context object. We attach a `notfound` flag to
    // the context to tell the server to change the response status
    // code into a 404.
    this.props.staticContext.notfound = true;
  }

  render() {
    const {
      history,
      routeConfiguration,
      marketplaceName,
      isKeywordSearch,
      intl,
      scrollingDisabled,
      hideSomeSections,
    } = this.props;

    const title = intl.formatMessage({
      id: 'NilfluenceScorePage.title',
    });

    const handleSearchSubmit = values => {
      const { keywords, location } = values;
      const { search, selectedPlace } = location || {};
      const { origin, bounds } = selectedPlace || {};
      const searchParams = keywords ? { keywords } : { address: search, origin, bounds };
      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
    };

    return (
      <Page title={title} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn
          {...(!hideSomeSections && {
            topbar: <TopbarContainer />,
            footer: <FooterContainer />,
          })}
        >
          <div className={css.root}>
            <div className={css.content}>
              <h1 className={css.sectionTitle}>NILfluence Score</h1>
              <p>
                The NILfluence Score quantifies an athlete’s digital influence across platforms like
                Instagram, TikTok, X, and YouTube, offering a data-driven measure of their
                marketability. By analyzing engagement and key popularity indicators, it helps
                brands assess ROI, determine fair market value, and optimize NIL partnerships.
              </p>
              <div className={css.chartImg}>
                <img src={chart1} alt="img" />
              </div>
              <div className={css.sectionContainer}>
                <h2>Follower Breakdown</h2>
                <div className={css.chartImg}>
                  <img src={chart2} alt="img" />
                </div>
              </div>
              <div className={css.sectionContainer}>
                <h2>Industry Alignment</h2>
                <p>
                  This talent would be perfect for the following industries, but could certainly fit
                  into other industries as well:
                </p>
                <ul>
                  <li>Entertainment</li>
                  <li>Motion Pictures and Film</li>
                  <li>Performing Arts</li>
                  <li>Defense and Space</li>
                  <li>Military</li>
                </ul>
              </div>
              <div className={css.sectionContainer}>
                <h2>
                  <FormattedMessage id="NilfluenceScorePage.costEstimatorHeading" />
                </h2>
                <p>
                  <FormattedMessage id="NilfluenceScorePage.costEstimatorDescription" />
                </p>
                <table>
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th>Totals</th>
                      <th>Total Engagement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Total Followers', '362,000', '3.74%'],
                      ['Total Likes/Posts', '4,350', ''],
                      ['Total Comments/Post', '2,800', ''],
                      ['Total Shares/Post', '6,400', ''],
                    ].map(([label, total, eng], i) => (
                      <tr key={i}>
                        <td>{label}</td>
                        <td>{total}</td>
                        <td>{eng || '\u00A0'}</td>
                      </tr>
                    ))}
                    <tr>
                      <th>Approximate Post Value/Cost</th>
                      <th>$13,550.00</th>
                      <th>&nbsp;</th>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className={css.sectionContainer}>
                <h2>
                  <FormattedMessage id="NilfluenceScorePage.roiEstimatorHeading" />
                </h2>
                <p>
                  <FormattedMessage id="NilfluenceScorePage.roiEstimatorDescription" />
                </p>

                <table>
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Profit Per Product Sold', value: '$45.00' },
                      { label: 'Products Needed to be Sold To Breakeven', value: '522' },
                      { label: 'Purchase Percent of Total Engagements', value: '5.00%' },
                      { label: 'Estimated Products Sold', value: '1,175' },
                      { label: 'Revenue', value: '$52,875.00' },
                      { isHeader: true, label: 'ROI', value1: '$29,375.00', value2: '55.56%' },
                    ].map((row, i) => (
                      <tr key={i}>
                        {row.isHeader ? (
                          <>
                            <th>{row.label}</th>
                            <th>{row.value1}</th>
                            <th>{row.value2}</th>
                          </>
                        ) : (
                          <>
                            <td>{row.label}</td>
                            <td>{row.value}</td>
                            <td>&nbsp;</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

NilfluenceScorePageComponent.defaultProps = {
  staticContext: {},
};

NilfluenceScorePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  marketplaceName: string.isRequired,
  isKeywordSearch: bool.isRequired,

  // The context object from StaticRouter,
  // it should be manually passed if this page is rendered inside other route.
  staticContext: object,

  // from useIntl
  intl: intlShape.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

const EnhancedNilfluenceScorePage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const intl = useIntl();
  const scrollingDisabled = useSelector(isScrollingDisabled);
  return (
    <NilfluenceScorePageComponent
      routeConfiguration={routeConfiguration}
      marketplaceName={config.marketplaceName}
      isKeywordSearch={isMainSearchTypeKeywords(config)}
      history={history}
      intl={intl}
      scrollingDisabled={scrollingDisabled}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const NilfluenceScorePage = compose(connect(mapStateToProps))(EnhancedNilfluenceScorePage);

export default NilfluenceScorePage;
